/** Overall ceiling. Do not lower — a cold start plus a slow account reaches ~60s. */
export const MEETING_PREP_OVERALL_TIMEOUT_MS = 120_000;
/** Silence on an event stream. Reset on every SSE frame. Not used for JSON responses. */
export const MEETING_PREP_IDLE_TIMEOUT_MS = 60_000;

/**
 * Split a buffer into complete SSE frames (blank-line separated).
 * Do not split on a single newline — a data payload may contain newlines.
 */
export function takeSseFrames(buffer) {
  const normalized = String(buffer || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const frames = [];
  let rest = normalized;
  let i;
  while ((i = rest.indexOf('\n\n')) !== -1) {
    frames.push(rest.slice(0, i));
    rest = rest.slice(i + 2);
  }
  return { frames, rest };
}

export function parseSseFrame(frame) {
  let event = 'message';
  const dataLines = [];
  for (const line of String(frame || '').split('\n')) {
    if (!line || line.startsWith(':')) continue;
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
  }
  const raw = dataLines.join('\n');
  let data = null;
  if (raw) data = JSON.parse(raw);
  return { event, data };
}

function abortError() {
  const err = new Error('The operation was aborted');
  err.name = 'AbortError';
  return err;
}

function throwIfAborted(signal) {
  if (signal?.aborted) throw abortError();
}

async function readEventStream(response, { onProgress, onActivity, signal }) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let result;

  const handleFrame = (frame) => {
    if (!String(frame || '').trim()) return;
    onActivity?.();
    const parsed = parseSseFrame(frame);
    if (parsed.event === 'progress') {
      if (typeof onProgress === 'function') onProgress(parsed.data || {});
      return;
    }
    if (parsed.event === 'error') {
      const message = parsed.data && typeof parsed.data.message === 'string' && parsed.data.message
        ? parsed.data.message
        : 'Meeting prep failed';
      throw new Error(message);
    }
    if (parsed.event === 'result') {
      result = parsed.data;
    }
  };

  try {
    while (result === undefined) {
      throwIfAborted(signal);
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const split = takeSseFrames(buf);
      buf = split.rest;
      for (const frame of split.frames) handleFrame(frame);
    }
    if (result === undefined) {
      buf += decoder.decode();
      if (buf.trim()) {
        const split = takeSseFrames(`${buf}\n\n`);
        for (const frame of split.frames) handleFrame(frame);
      }
    }
  } finally {
    reader.cancel?.().catch(() => {});
  }

  if (result === undefined) throw new Error('Meeting prep ended without a result');
  return result;
}

/**
 * POST /api/meeting-prep. SSE when the server says so; otherwise the JSON body
 * the endpoint returns today. Branch on the response Content-Type only.
 */
export async function requestMeetingPrep({ apiBase, headers, body, signal, onProgress }) {
  const controller = new AbortController();
  const onExternalAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', onExternalAbort, { once: true });
  }

  let idleTimer;
  const clearIdle = () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = null;
  };
  const armIdle = () => {
    clearIdle();
    idleTimer = setTimeout(() => controller.abort(), MEETING_PREP_IDLE_TIMEOUT_MS);
  };
  const overallTimer = setTimeout(() => controller.abort(), MEETING_PREP_OVERALL_TIMEOUT_MS);

  try {
    throwIfAborted(controller.signal);
    const response = await fetch(`${apiBase}/api/meeting-prep`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...headers,
        Accept: 'text/event-stream, application/json',
      },
      credentials: 'include',
      signal: controller.signal,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const ct = response.headers.get('content-type') || '';
    if (!ct.includes('text/event-stream')) {
      return await response.json();
    }

    armIdle();
    return await readEventStream(response, {
      onProgress,
      onActivity: armIdle,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(overallTimer);
    clearIdle();
    if (signal) signal.removeEventListener('abort', onExternalAbort);
  }
}
